import React, { ReactElement, useRef } from 'react';
import { Box, Text, VStack } from '@chakra-ui/react';
import { NovaIconButton } from 'components/ui/NovaIconButton';
import { DeleteIcon } from 'assets/icons/v2';
import { BlockInput } from '../../../common/BlockInput';
import { TQBlockProps, TQBlockUpdate } from '../TextQuestionBlock';

type Props = TQBlockProps & {
  onBlockUpdate: (updates: TQBlockUpdate) => void;
  onDelete?: () => void;
};

export const TeacherTQBlock = ({ block, editor, onBlockUpdate, onDelete }: Props): ReactElement => {
  const [question, setQuestion] = React.useState<string>(block.props.question || '');
  const [correctAnswer, setCorrectAnswer] = React.useState<string>(block.props.correctAnswer || '');
  const questionRef = useRef<HTMLTextAreaElement>(null);
  const answerRef = useRef<HTMLTextAreaElement>(null);

  const onAnswerChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
    const newAnswer = e.target.value;
    setCorrectAnswer(newAnswer);
    onBlockUpdate({ correctAnswer: newAnswer });
  };

  const onQuestionChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
    const newQuestion = e.target.value;
    setQuestion(newQuestion);
    onBlockUpdate({ question: newQuestion });
  };

  const switchToAnswer = (): void => {
    if (answerRef.current) {
      answerRef.current.focus();
      answerRef.current.setSelectionRange(
        answerRef.current.value.length,
        answerRef.current.value.length
      );
    }
  };

  const jumpToEditor = (): void => {
    // Focus the parent editor
    editor.focus();

    // Move cursor to the end of the current block
    editor.setTextCursorPosition(block, 'end');

    // Create a new block by simulating Enter key press
    const enterEvent = new KeyboardEvent('keydown', {
      key: 'Enter',
      code: 'Enter',
      keyCode: 13,
      which: 13,
      bubbles: true,
      cancelable: true,
      composed: true,
    });

    // Dispatch the event on the editor's root element
    editor.domElement?.dispatchEvent(enterEvent);
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLTextAreaElement>,
    type: 'question' | 'answer'
  ): void => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (type === 'question') {
        switchToAnswer();
      } else {
        jumpToEditor();
      }
    }
  };

  return (
    <VStack
      border="default"
      borderColor="border.accent"
      borderRadius="l"
      p="l"
      gap="l"
      width="100%"
      alignItems="flex-start"
      contentEditable={false}
      bg="grey.white"
    >
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" width="100%">
        <Text textStyle="body.m.medium" color="text.primary">
          Textfrage
        </Text>
        {onDelete && (
          <NovaIconButton
            aria-label="Frage löschen"
            variant="primary"
            size="m"
            onClick={onDelete}
            _hover={{
              '& svg': {
                color: 'red.500',
              },
            }}
            boxSize="18px"
            fontSize="18px"
            lineHeight="0"
            minWidth="0px"
            icon={DeleteIcon}
            iconProps={{ fill: 'transparent', color: 'icon.quaternary' }}
          />
        )}
      </Box>
      <BlockInput
        label="Frage"
        name="question"
        value={question}
        initialRows={1}
        onChange={onQuestionChange}
        onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => handleKeyDown(e, 'question')}
        placeholder="Gib deine Frage hier ein..."
        ref={questionRef}
        showLineBreakHint
      />
      <BlockInput
        label="Antwort"
        name="answer"
        value={correctAnswer}
        initialRows={3}
        onChange={onAnswerChange}
        onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => handleKeyDown(e, 'answer')}
        placeholder="Gib deine Musterlösung hier ein..."
        ref={answerRef}
        showLineBreakHint
      />
    </VStack>
  );
};
